import { useEffect, useRef, useState } from "react";
import { Send, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../lib/i18n";
import { api, formatError } from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { FileUploader, AttachmentList } from "../components/FileUploader";

export default function Messages() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [threads, setThreads] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [activeName, setActiveName] = useState("");
  const [msgs, setMsgs] = useState([]);
  const [body, setBody] = useState("");
  const [pendingFiles, setPendingFiles] = useState([]);
  const endRef = useRef(null);

  const loadThreads = () => api.get("/messages/threads").then(({ data }) => setThreads(data)).catch(() => {});

  useEffect(() => {
    loadThreads();
    if (user?.role === "patient") {
      api.get("/doctors").then(({ data }) => {
        setDoctors(data);
        if (data.length === 1 && !activeId) {
          setActiveId(data[0].id);
          setActiveName(data[0].name);
        }
      });
    }
    const i = setInterval(loadThreads, 8000);
    return () => clearInterval(i);
  }, [user]);

  useEffect(() => {
    if (!activeId) return;
    const fetchMsgs = () =>
      api.get("/messages", { params: { with_user: activeId } })
        .then(({ data }) => setMsgs(data)).catch(() => {});
    fetchMsgs();
    const i = setInterval(fetchMsgs, 5000);
    return () => clearInterval(i);
  }, [activeId]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const send = async (e) => {
    e.preventDefault();
    if ((!body.trim() && pendingFiles.length === 0) || !activeId) return;
    try {
      await api.post("/messages", {
        to_user_id: activeId,
        body: body.trim() || "📎 Archivos adjuntos",
        attachment_ids: pendingFiles.map((f) => f.id),
      });
      setBody("");
      setPendingFiles([]);
      const { data } = await api.get("/messages", { params: { with_user: activeId } });
      setMsgs(data);
      loadThreads();
    } catch (err) { toast.error(formatError(err)); }
  };

  const openThread = (otherId, otherName) => {
    setActiveId(otherId);
    setActiveName(otherName);
  };

  return (
    <div className="fade-up h-[calc(100vh-180px)] grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="messages-page">
      <Card className="md:col-span-1 overflow-hidden flex flex-col">
        <CardContent className="p-0 flex-1 overflow-y-auto">
          {threads.length === 0 && doctors.length === 0 && (
            <div className="text-sm text-muted-foreground p-4">{t("no_messages")}</div>
          )}
          {/* For patients, show doctors as initial contacts only if no thread exists yet */}
          {user?.role === "patient" && threads.length === 0 && doctors.map((d) => (
              <button
                key={d.id}
                onClick={() => openThread(d.id, d.name)}
                className={`w-full text-left px-4 py-3 border-b border-border flex items-center gap-3 hover:bg-[#F3F1EC] ${activeId === d.id ? "bg-[#F3F1EC]" : ""}`}
                data-testid={`thread-${d.id}`}
              >
                <div className="w-9 h-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-medium">
                  {d.name?.[0]}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{d.name}</div>
                  <div className="text-xs text-muted-foreground truncate">{d.specialty || "Medicina de Urgencias y Emergencias"}</div>
                </div>
              </button>
          ))}
          {threads.map((th) => (
            <button
              key={th.thread_id}
              onClick={() => openThread(th.other_user_id, th.other_name)}
              className={`w-full text-left px-4 py-3 border-b border-border flex items-center gap-3 hover:bg-[#F3F1EC] ${activeId === th.other_user_id ? "bg-[#F3F1EC]" : ""}`}
              data-testid={`thread-${th.other_user_id}`}
            >
              <div className="w-9 h-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-medium">
                {th.other_name?.[0]}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-medium truncate">{th.other_name}</div>
                  {th.unread > 0 && (
                    <span className="text-[10px] bg-secondary text-white rounded-full px-2 py-0.5 ml-2">{th.unread}</span>
                  )}
                </div>
                <div className="text-xs text-muted-foreground truncate">{th.last_body}</div>
              </div>
            </button>
          ))}
        </CardContent>
      </Card>

      <Card className="md:col-span-2 flex flex-col overflow-hidden">
        {!activeId ? (
          <div className="flex-1 flex items-center justify-center text-muted-foreground">
            <div className="text-center">
              <MessageSquare className="w-12 h-12 mx-auto mb-2 opacity-40" />
              <div>{t("no_messages")}</div>
            </div>
          </div>
        ) : (
          <>
            <div className="px-4 py-3 border-b border-border bg-[#F3F1EC]">
              <div className="font-medium" data-testid="active-thread-name">{activeName}</div>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-background" data-testid="msgs-list">
              {msgs.length === 0 && (
                <div className="text-center text-sm text-muted-foreground py-6">— Inicio de conversación —</div>
              )}
              {msgs.map((m) => {
                const mine = m.from_user_id === user.id;
                return (
                  <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.id}`}>
                    <div className={`max-w-[75%] rounded-2xl px-4 py-2 text-sm ${mine ? "bg-primary text-primary-foreground rounded-br-sm" : "bg-[#F3F1EC] text-foreground rounded-bl-sm"}`}>
                      <div className="whitespace-pre-wrap break-words">{m.body}</div>
                      {m.attachments && m.attachments.length > 0 && <AttachmentList items={m.attachments} />}
                      <div className={`text-[10px] mt-1 opacity-70 ${mine ? "text-right" : ""}`}>
                        {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={endRef} />
            </div>
            <form onSubmit={send} className="p-3 border-t border-border space-y-2">
              <FileUploader files={pendingFiles} setFiles={setPendingFiles} purpose="message" testid="msg-attach" />
              <div className="flex gap-2">
                <Input value={body} onChange={(e) => setBody(e.target.value)} placeholder={t("type_message")} data-testid="msg-input" />
                <Button type="submit" disabled={!body.trim() && pendingFiles.length === 0} className="bg-primary hover:bg-primary/90" data-testid="msg-send-btn">
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </form>
          </>
        )}
      </Card>
    </div>
  );
}
